"use client";

import React from "react";

import type { BrandOption } from "@/components/results/filters/BrandsFilter";
import { FiltersContent } from "@/components/results/FiltersContent";
import type { CategoryNode } from "@/hooks/useResultsFilters";

import type { ShopperFilters } from "@/lib/shopper-search";

interface ResultsFiltersPanelProps {
  category: {
    categories: CategoryNode[];
    navigationStack: Array<{ name: string; path: string }>;
    onNavigationChange: (stack: Array<{ name: string; path: string }>) => void;
    onCategoryNavigate: (path: string) => void;
    isHierarchyNavigationEnabled?: boolean;
    currentCategoryPath?: string;
    selectedLeafPaths: string[];
    onLeafToggle: (path: string) => void;
    isLoadingTaxonomy: boolean;
  };
  price: {
    min: number;
    max: number;
    range: [number, number];
    onChange: (range: [number, number]) => void;
  };
  colors: {
    selected: string[];
    onChange: (colors: string[]) => void;
  };
  brands: {
    available: BrandOption[];
    selected: string[];
    onChange: (brands: string[]) => void;
    isLoading: boolean;
  };
  similarBrands: {
    selected: string[];
    onChange: (brands: string[]) => void;
  };
  advanced: {
    searchParams: ShopperFilters | null;
    selectedAttributes: Map<string, Set<string>>;
    onToggle: (attributeKey: string, value: string) => void;
  };
  visibility?: {
    showBrandsFilter?: boolean;
    showSimilarToBrandsFilter?: boolean;
  };
}

export function ResultsFiltersPanel({
  category,
  price,
  colors,
  brands,
  similarBrands,
  advanced,
  visibility,
}: ResultsFiltersPanelProps): React.ReactElement {
  return (
    <FiltersContent
      categories={category.categories}
      navigationStack={category.navigationStack}
      onNavigationChange={category.onNavigationChange}
      onCategoryNavigate={category.onCategoryNavigate}
      isHierarchyNavigationEnabled={category.isHierarchyNavigationEnabled}
      currentCategoryPath={category.currentCategoryPath}
      selectedLeafPaths={category.selectedLeafPaths}
      onLeafToggle={category.onLeafToggle}
      isLoadingTaxonomy={category.isLoadingTaxonomy}
      priceMin={price.min}
      priceMax={price.max}
      priceRange={price.range}
      onPriceChange={price.onChange}
      selectedColors={colors.selected}
      onColorChange={colors.onChange}
      availableBrands={brands.available}
      selectedBrands={brands.selected}
      onBrandChange={brands.onChange}
      isLoadingBrands={brands.isLoading}
      selectedSimilarToBrands={similarBrands.selected}
      onSimilarToBrandChange={similarBrands.onChange}
      showBrandsFilter={visibility?.showBrandsFilter}
      showSimilarToBrandsFilter={visibility?.showSimilarToBrandsFilter}
      searchParams={advanced.searchParams}
      selectedAdvancedAttributes={advanced.selectedAttributes}
      onAdvancedAttributeToggle={advanced.onToggle}
    />
  );
}
